import { MUITextField } from "@abb/arcadia-ui-react"
import { zodResolver } from "@hookform/resolvers/zod"
import { StatusCodes } from "http-status-codes"
import { useCallback, useContext, useEffect, useMemo } from "react"
import { Controller, ControllerRenderProps, useForm } from "react-hook-form"
import { toast } from "react-toastify"
import { z } from "zod"
import { digitalManagerApi } from "../../../../../services/api"
import {
  DiMaDetailsContent,
  DiMaDetailsContentProps,
} from "../../../../components/DiMaDetailsContent"
import { DiMaProgress } from "../../../../components/DiMaProgress"
import { UserContext } from "../../../../context/UserContext"
import {
  AccessLevel,
  DimaFeatureShortNameEnum,
  hasUserAccessToFeature,
} from "../../../../models/Authorization"
import {
  DeprecatedDeviceGet,
  DeprecatedDevicePut,
} from "../../../../models/DeprecatedDevice"

const validationSchema = z.object({
  deviceSerialNumber: z.string().min(1, "Device Serial Number is required"),
  certificateSerialNumber: z.string().min(1, "Certificate Serial Number is required"),
})

const returnURL = `/deprecated-devices`

export function DeprecatedDeviceDetails(props: {
  readonly deprecatedDevice: DeprecatedDeviceGet
  readonly setDeprecatedDevice: (updatedDeprecatedDevice: DeprecatedDeviceGet) => void
}) {
  const { user, isLoadingUser } = useContext(UserContext)

  const { deprecatedDevice, setDeprecatedDevice } = props

  const formMethods = useForm<DeprecatedDevicePut>({
    resolver: zodResolver(validationSchema),
    mode: "onChange",
    reValidateMode: "onChange",
  })

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = formMethods

  useEffect(() => {
    reset({
      deviceSerialNumber: deprecatedDevice.deviceSerialNumber,
      certificateSerialNumber: deprecatedDevice.certificateSerialNumber,
    })
  }, [deprecatedDevice, reset])

  const onSubmit = useCallback(
    (values: DeprecatedDevicePut) =>
      digitalManagerApi
        .put<DeprecatedDeviceGet>(`/deprecatedDevices/${deprecatedDevice.id}`, values)
        .then((res) => {
          if (res.status === StatusCodes.OK) {
            toast.success("Deprecated device updated")
            setDeprecatedDevice(res.data)
          }
        })
        .catch(() => {
          toast.error("Cannot update deprecated device")
          throw new Error("Cannot update deprecated device")
        }),
    [deprecatedDevice.id, setDeprecatedDevice]
  )

  const canWrite = useMemo(
    () =>
      user
        ? hasUserAccessToFeature(
            user.authorizations,
            DimaFeatureShortNameEnum.DD,
            AccessLevel.Write
          )
        : false,
    [user]
  )

  const handleTextChange = useCallback(
    (
      field: ControllerRenderProps<
        DeprecatedDevicePut,
        "deviceSerialNumber" | "certificateSerialNumber"
      >
    ) =>
      (value: string) =>
        field.onChange(value),
    []
  )

  const renderTextFieldDeviceSerialNumber = useCallback(
    ({
      field,
    }: {
      field: ControllerRenderProps<DeprecatedDevicePut, "deviceSerialNumber">
    }) => (
      <MUITextField
        id="deviceSerialNumber"
        type="text"
        size={32}
        name={field.name}
        value={field.value ?? ""}
        onChange={handleTextChange(field)}
        onBlur={field.onBlur}
        isDisabled={!canWrite}
        errors={
          typeof errors.deviceSerialNumber?.message === "string"
            ? { deviceSerialNumber: { message: errors.deviceSerialNumber.message } }
            : undefined
        }
      />
    ),
    [canWrite, errors.deviceSerialNumber, handleTextChange]
  )

  const renderTextFieldCertificateSerialNumber = useCallback(
    ({
      field,
    }: {
      field: ControllerRenderProps<DeprecatedDevicePut, "certificateSerialNumber">
    }) => (
      <MUITextField
        id="certificateSerialNumber"
        type="text"
        size={32}
        name={field.name}
        value={field.value ?? ""}
        onChange={handleTextChange(field)}
        onBlur={field.onBlur}
        isDisabled={!canWrite}
        errors={
          typeof errors.certificateSerialNumber?.message === "string"
            ? {
                certificateSerialNumber: {
                  message: errors.certificateSerialNumber.message,
                },
              }
            : undefined
        }
      />
    ),
    [canWrite, errors.certificateSerialNumber, handleTextChange]
  )

  if (isLoadingUser || !user) {
    return <DiMaProgress />
  }

  const detailsContentProps: DiMaDetailsContentProps<DeprecatedDevicePut> = {
    formMethods,
    canWrite,
    inlineEdit: true,
    listItems: [
      {
        id: 0,
        label: "Device Serial Number",
        displayItem: (
          <div style={{ width: "100%" }}>{deprecatedDevice.deviceSerialNumber}</div>
        ),
        editItem: (
          <Controller
            name="deviceSerialNumber"
            control={control}
            render={renderTextFieldDeviceSerialNumber}
          />
        ),
      },
      {
        id: 1,
        label: "Certificate Serial Number",
        displayItem: (
          <div style={{ width: "100%" }}>{deprecatedDevice.certificateSerialNumber}</div>
        ),
        editItem: (
          <Controller
            name="certificateSerialNumber"
            control={control}
            render={renderTextFieldCertificateSerialNumber}
          />
        ),
      },
    ],
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <DiMaDetailsContent {...detailsContentProps} onSubmit={onSubmit} />
    </form>
  )
}
